import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * Instance ACL — the /v1/acl/** admin surface (OPSFL-FSD-IAM-UC1 §4.7; wire shapes in
 * contracts/proposed/openapi/product-api-acl-paths.v1.yaml). A thin contract-faithful transport
 * like teamAccess.ts: every mutation carries an `ol-idempotency-key`; THE SERVER IS THE GATE
 * (acl.read: hub_admin/hub_operator/auditor; the three write keys are hub_admin-only AND step-up
 * lanes — a JWT without a fresh acr=loa2 is refused 403 STEPUP_REQUIRED, which the panels surface
 * verbatim, never swallow).
 *
 * OPSFL-FLOWOPS-1 C3 composes two of these into the "Assign to spoke" fence: a CONFINE row for
 * (SPOKE s, flow) — s sees/starts nothing until granted — plus an ALLOW row per flow the spoke
 * may see (view) and start (work — runs.start is ACL-mapped to (flow, work) since C1).
 *
 * Server refusals the UI renders (ApiError.code): 403 STEPUP_REQUIRED / ACL_SELF_GRANT_FORBIDDEN;
 * 422 ACL_GROUP_RESERVED / ACL_AUDITOR_MUTATING / ACL_CAPABILITY_EXCEEDS_ROLE /
 * ACL_PROJECT_UNKNOWN / LAST_HUB_ADMIN_PROTECTED; 400 for a bad reasonCode/capability.
 */

export type GranteeType = 'USER' | 'ROLE' | 'SPOKE' | 'PROJECT';
export type AclResourceType = 'work_item' | 'flow' | 'entity' | 'dataroom_invite';
export type AclCapability = 'view' | 'work' | 'approve' | 'export';
export type AclReasonCode =
  | 'CASE_ASSIGNMENT'
  | 'SUPERVISION'
  | 'ESCALATION'
  | 'TEMP_COVER'
  | 'MIGRATION'
  | 'SCOPE_CONFINEMENT'
  | 'OTHER';

export const ACL_REASON_CODES: AclReasonCode[] = [
  'CASE_ASSIGNMENT', 'SUPERVISION', 'ESCALATION', 'TEMP_COVER', 'MIGRATION',
  'SCOPE_CONFINEMENT', 'OTHER',
];

export interface GrantView {
  grantId: string;
  grantKind: 'ALLOW' | 'CONFINE';
  granteeType: GranteeType;
  grantee: string;
  resourceType: AclResourceType;
  /** null for CONFINE rows (a confinement is per type, not per instance). */
  resourceId: string | null;
  capabilities: AclCapability[];
  reasonCode: string;
  caseRef: string | null;
  grantedBy: string;
  grantedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revokeReason: string | null;
}

export interface GrantFilter {
  granteeType?: GranteeType;
  grantee?: string;
  resourceType?: AclResourceType;
  resourceId?: string;
}

/** True while the row still bites: not revoked and not past its expiry. */
export function isLiveGrant(g: GrantView, now: number = Date.now()): boolean {
  if (g.revokedAt) return false;
  if (g.expiresAt && Date.parse(g.expiresAt) <= now) return false;
  return true;
}

/** GET /v1/acl/grants — includes revoked rows (no-delete: history is queryable). */
export async function listGrants(filter: GrantFilter = {}): Promise<GrantView[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ grants: GrantView[] }>>('/v1/acl/grants', {
    params: {
      granteeType: filter.granteeType,
      grantee: filter.grantee,
      resourceType: filter.resourceType,
      resourceId: filter.resourceId,
    },
  });
  const body = unwrap(data);
  return Array.isArray(body?.grants) ? body.grants : [];
}

export interface CreateGrantRequest {
  granteeType: GranteeType;
  grantee: string;
  resourceType: AclResourceType;
  resourceId: string;
  capabilities: AclCapability[];
  reasonCode: AclReasonCode;
  caseRef?: string;
  /** RFC-3339; omitted = open-ended (audited). */
  expiresAt?: string;
}

/** POST /v1/acl/grants — an ALLOW row (acl.grants.write, STEP_UP). */
export async function createGrant(req: CreateGrantRequest): Promise<GrantView> {
  const { data } = await apiClient.post<ApiEnvelope<GrantView>>('/v1/acl/grants', req,
    idempotentConfig());
  return unwrap(data);
}

export interface ConfineRequest {
  granteeType: GranteeType;
  grantee: string;
  resourceType: AclResourceType;
  reasonCode: AclReasonCode;
  expiresAt?: string;
}

/**
 * POST /v1/acl/confinements — a CONFINE row (acl.confinements.write, STEP_UP). One LIVE
 * confinement per (grantee, type) — the store enforces it with a partial unique index; callers
 * should check {@link listGrants} first (a second live CONFINE is refused by the database).
 */
export async function confine(req: ConfineRequest): Promise<GrantView> {
  const { data } = await apiClient.post<ApiEnvelope<GrantView>>('/v1/acl/confinements', req,
    idempotentConfig());
  return unwrap(data);
}

/** POST /v1/acl/grants/{id}/revoke — UPDATE-only, bites on the grantee's next request. */
export async function revokeGrant(grantId: string, reason?: string): Promise<GrantView> {
  const { data } = await apiClient.post<ApiEnvelope<GrantView>>(
    `/v1/acl/grants/${encodeURIComponent(grantId)}/revoke`,
    reason ? { reason } : {},
    idempotentConfig(),
  );
  return unwrap(data);
}
